import { useEffect, useState, type ReactNode } from 'react';
import { clearSession } from '../api/client.js';
import { logout } from '../api/endpoints.js';
import { useApi } from '../api/hooks.js';
import { useSignedIn, useSignoutReason } from '../api/session-hooks.js';
import { ScreenBoundary } from '../ui/boundary.js';
import { getOperatorMe, getReviewQueue, type OperatorMe } from './api.js';
import { queueTotal } from './forms.js';
import { adminHash, parseAdminHash, type AdminRoute, type AdminSection } from './routes.js';
import { AdminSignIn } from './screens/login.js';
import { OverviewScreen } from './screens/overview.js';
import { ReviewScreen } from './screens/review.js';
import { ThreatsScreen } from './screens/threats.js';
import { ScoresScreen } from './screens/scores.js';
import { ArticlesScreen } from './screens/articles.js';
import { Trouble } from './ui.js';
import './admin.css';

/**
 * The admin console at /admin (spec 2026-08-29-admin-panel-web-design.md).
 *
 * Three gates in order: no session → the operator sign-in; a session → GET
 * /v1/admin/me; 403 NOT_AN_OPERATOR → a screen that says so, never the
 * console; 200 → the shell. The grant is re-checked by the backend on every
 * request, so "Check again" after a grant needs no new sign-in.
 */

function useHash(): string {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

function signOut(): void {
  // Best-effort revoke; the session dies locally regardless, and the next
  // sign-in lands on the overview rather than wherever this one left off.
  logout().catch(() => undefined);
  clearSession();
  window.history.replaceState(null, '', adminHash('overview'));
}

export function Brand() {
  return (
    <span className="cr-brand-text">
      <strong>ImageShield</strong>
      <span className="cr-eyebrow">Admin console</span>
    </span>
  );
}

export function BrandMark() {
  return (
    <span className="cr-brand-mark" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.8 13 4v4.5c0 2.9-2 4.5-5 5.5-3-1-5-2.6-5-5.5V4z" fill="#fff" />
      </svg>
    </span>
  );
}

export function AdminApp() {
  const signedIn = useSignedIn();
  const signoutReason = useSignoutReason();
  const hash = useHash();

  if (!signedIn) {
    return (
      <div className="cr-root cr-solo">
        <ScreenBoundary screen="admin-sign-in">
          <AdminSignIn notice={signoutReason} />
        </ScreenBoundary>
      </div>
    );
  }

  return <OperatorGate route={parseAdminHash(hash)} />;
}

function OperatorGate({ route }: { route: AdminRoute }) {
  const me = useApi(getOperatorMe, []);

  if (me.data !== null) return <Shell me={me.data} route={route} />;

  return (
    <div className="cr-root cr-solo">
      <div className="cr-login">
        <div className="cr-brand">
          <BrandMark />
          <Brand />
        </div>
        {me.error === null ? (
          <p>Checking your access…</p>
        ) : me.error.code === 'NOT_AN_OPERATOR' ? (
          <>
            <h1 className="cr-login-title">Your account does not have operator access yet</h1>
            <p>
              You are signed in, but this account has not been granted access to the admin console.
              Ask the ImageShield engineering team to grant it — then choose <strong>Check again</strong>.
              You will not need to sign in again.
            </p>
          </>
        ) : (
          <>
            <h1 className="cr-login-title">We could not check your access</h1>
            <Trouble error={me.error} />
          </>
        )}
        <div className="cr-actions">
          <button type="button" className="cr-btn primary" onClick={me.reload} disabled={me.loading}>
            {me.loading ? 'Checking…' : 'Check again'}
          </button>
          <button type="button" className="cr-btn ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

const NAV: { section: AdminSection; label: string }[] = [
  { section: 'overview', label: 'Overview' },
  { section: 'review', label: 'Review' },
  { section: 'threats', label: 'Threat events' },
  { section: 'scores', label: 'Score lookup' },
  { section: 'articles', label: 'Articles' },
];

/** Queue depth for the rail badge — a minute is plenty for a number in a sidebar. */
const RAIL_QUEUE_POLL_MS = 60_000;

function render(route: AdminRoute): ReactNode {
  switch (route.section) {
    case 'overview':
      return <OverviewScreen />;
    case 'review':
      return <ReviewScreen />;
    case 'threats':
      return <ThreatsScreen />;
    case 'scores':
      return <ScoresScreen userRef={route.id} />;
    case 'articles':
      return <ArticlesScreen id={route.id} />;
  }
}

function Shell({ me, route }: { me: OperatorMe; route: AdminRoute }) {
  const queue = useApi(getReviewQueue, [], { pollMs: RAIL_QUEUE_POLL_MS });
  const pending = queue.data === null ? null : queueTotal(queue.data);

  return (
    <div className="cr-root">
      <aside className="cr-rail">
        <a className="cr-brand" href={adminHash('overview')}>
          <BrandMark />
          <Brand />
        </a>
        <nav className="cr-nav" aria-label="Console sections">
          {NAV.map((entry) => (
            <a
              key={entry.section}
              href={adminHash(entry.section)}
              className="cr-nav-link"
              aria-current={entry.section === route.section}
            >
              {entry.label}
              {entry.section === 'review' && pending !== null && (
                <span className={pending > 0 ? 'cr-count hot' : 'cr-count'} title="Matches waiting for a decision">
                  {pending}
                </span>
              )}
            </a>
          ))}
        </nav>
        <div className="cr-rail-foot">
          <div className="cr-operator">
            <span className="cr-eyebrow">Signed in as</span>
            <strong>{me.display_name}</strong>
            <span className="cr-fine">Your name is recorded on every change you make.</span>
          </div>
          <div className="cr-actions">
            <button type="button" className="cr-btn ghost" onClick={signOut}>
              Sign out
            </button>
            <a className="cr-btn ghost" href="/">
              Open the app
            </a>
          </div>
        </div>
      </aside>
      <main className="cr-main" key={route.section}>
        <ScreenBoundary screen={`admin-${route.section}`}>{render(route)}</ScreenBoundary>
      </main>
    </div>
  );
}
